import {
  Archive, Award, BarChart3, Bell, Boxes, Building2, Calendar, CalendarDays, Check, Church, ClipboardList, Clock, Coins,
  Compass, Copy, CreditCard, DollarSign, Download, Droplet, Edit, Eye, FileCode2, Files, FileText, Folder, FolderInput,
  FolderPlus, Heart, Home, KeyRound, Layers, LayoutDashboard, Link, Lock, LogOut, Mail, MapPin, MessageSquare, Monitor,
  Moon, Paintbrush, Palette, PenLine, Pencil, Pin, PinOff, Play, Plug, Plus, Power, PowerOff, RefreshCw, Search,
  Settings, Settings2, Share2, Sparkles, Star, Sun, Table2, Tag, Tags, Target, Trash2, Upload, User, UserCheck,
  UserMinus, UserPlus, Users, UserX, SunMoon, FileUp, FolderTree, Grid3x3,
  LibraryBig, LayoutGrid, Database, CheckCircle2, Link2, Workflow, Unplug, Activity,
} from 'lucide-react'

/** Rendered by ~/Developer/DTS/graphit-fluent3d/icons.py into public/icons3d/<name>-<px>.webp. */
export const ICON3D_NAMES = [
  'dashboards', 'metrics', 'data-entry', 'reports', 'assistant', 'integrations', 'settings', 'getting-started',
  'favorites', 'people', 'groups', 'campus', 'baptisms', 'salvations', 'calendar', 'target', 'volunteers',
  'giving', 'check-ins', 'hero-dashboard',
  'organization', 'branding', 'palette', 'location', 'key', 'billing', 'annotations', 'categories', 'partitions',
  'templates', 'lock', 'bell', 'mail', 'clock', 'folder', 'tag', 'import', 'check', 'search', 'globe',
  'number-tile', 'stat-list', 'scoreboard', 'stat-tiles', 'chart-line', 'chart-area', 'chart-bar', 'chart-pie',
  'chart-donut', 'text-block', 'section',
  'trash', 'pencil', 'copy', 'download', 'share', 'link', 'pin', 'archive', 'home', 'play', 'power', 'logout',
  'plus', 'refresh', 'eye', 'table', 'user', 'user-add', 'user-remove', 'sun', 'moon', 'monitor',
]

/** The lucide icon a 3D icon stands in for, where one exists. */
export const LUCIDE_TO_3D = new Map([
  [LayoutDashboard, 'dashboards'], [BarChart3, 'metrics'], [ClipboardList, 'data-entry'], [FileText, 'reports'],
  [Sparkles, 'assistant'], [Plug, 'integrations'], [Settings2, 'settings'], [Settings, 'settings'],
  [Compass, 'getting-started'], [Star, 'favorites'], [Users, 'people'], [Building2, 'organization'], [Church, 'campus'],
  [Droplet, 'baptisms'], [Heart, 'salvations'], [Calendar, 'calendar'], [CalendarDays, 'calendar'],
  [Target, 'target'], [Award, 'volunteers'], [Coins, 'giving'], [DollarSign, 'giving'],
  // Menu actions
  [Trash2, 'trash'], [Edit, 'pencil'], [Pencil, 'pencil'], [PenLine, 'pencil'], [Copy, 'copy'], [Download, 'download'],
  [Upload, 'import'], [Share2, 'share'], [Link, 'link'], [Pin, 'pin'], [PinOff, 'pin'], [Archive, 'archive'],
  [Home, 'home'], [Play, 'play'], [Power, 'power'], [PowerOff, 'power'], [LogOut, 'logout'], [Plus, 'plus'],
  [FolderPlus, 'folder'], [FolderInput, 'folder'], [Folder, 'folder'], [RefreshCw, 'refresh'], [Eye, 'eye'],
  [Table2, 'table'], [User, 'user'], [UserPlus, 'user-add'], [UserCheck, 'user-add'], [UserMinus, 'user-remove'],
  [UserX, 'user-remove'], [Sun, 'sun'], [Moon, 'moon'], [SunMoon, 'sun'], [Monitor, 'monitor'], [Mail, 'mail'], [Clock, 'clock'],
  [MessageSquare, 'annotations'], [FileCode2, 'templates'], [Files, 'templates'], [Check, 'check'], [Tag, 'tag'],
  [Tags, 'tag'], [Lock, 'lock'], [Search, 'search'], [KeyRound, 'key'], [CreditCard, 'billing'], [Palette, 'palette'],
  [Paintbrush, 'branding'], [MapPin, 'location'], [Layers, 'categories'], [Boxes, 'partitions'], [Bell, 'bell'],
  [FileUp, 'import'], [FolderTree, 'folder'], [Grid3x3, 'table'], [LibraryBig, 'templates'], [LayoutGrid, 'stat-tiles'],
  [Database, 'table'], [CheckCircle2, 'check'], [Link2, 'link'], [Workflow, 'integrations'], [Unplug, 'integrations'],
  [Activity, 'chart-line'],
])

/** Which section icon a page header gets, by route. Longest prefix wins. */
const SECTION_ICONS = [
  ['/settings/organization', 'organization'], ['/settings/branding', 'branding'], ['/settings/campuses', 'location'],
  ['/settings/chart-colors', 'palette'], ['/settings/users', 'people'], ['/settings/authentication', 'key'],
  ['/settings/categories', 'categories'], ['/settings/partitions', 'partitions'], ['/settings/integrations', 'integrations'],
  ['/settings/ai', 'assistant'], ['/settings/annotations', 'annotations'], ['/settings/subscription', 'billing'],
  ['/integrations', 'integrations'], ['/settings', 'settings'],
  ['/dashboards', 'dashboards'], ['/metrics', 'metrics'], ['/categories', 'categories'], ['/partitions', 'partitions'],
  ['/data-entry', 'data-entry'], ['/forms', 'data-entry'], ['/reports', 'reports'], ['/report-templates', 'templates'],
  ['/getting-started', 'getting-started'], ['/campuses', 'location'], ['/goals', 'target'], ['/users', 'people'],
  ['/profile', 'user'], ['/organizations', 'organization'], ['/setup', 'integrations'], ['/onboarding', 'getting-started'],
]

export function sectionIcon(pathname) {
  const match = SECTION_ICONS.filter(([path]) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b[0].length - a[0].length)[0]
  return match ? match[1] : null
}

/** A topic icon for a dashboard or metric from the words in its name (and category). */
const TOPICS = [
  [/baptis/i, 'baptisms'], [/salvation|decision|care|prayer|heart/i, 'salvations'],
  [/giv|donor|fund|tith|offering|financ|budget/i, 'giving'], [/check-?in|kids|nextgen|student|youth/i, 'check-ins'],
  [/small group|group|life group/i, 'groups'], [/volunteer|serving|serve team|\bteams?\b/i, 'volunteers'],
  [/scorecard|scoreboard|kpi/i, 'scoreboard'], [/worship|service/i, 'people'],
  [/guest|visitor|member|attend|people|assimilat|weekend|sunday/i, 'people'], [/campus|church|location/i, 'campus'],
  [/mission|outreach|global|nations/i, 'globe'], [/building|facilit|capital/i, 'organization'],
  [/goal|target|executive|overview|board/i, 'target'], [/event|calendar|class|weekly|monthly/i, 'calendar'],
  [/online|livestream|stream|view/i, 'metrics'],
]

export function topicIcon(...texts) {
  const text = texts.filter(Boolean).join(' ')
  const match = TOPICS.find(([pattern]) => pattern.test(text))
  return match ? match[1] : null
}

/** Stored icon values for a chosen 3D icon look like "3d:giving". */
export const ICON3D_PREFIX = '3d:'

export function icon3dValue(name) {
  return name ? `${ICON3D_PREFIX}${name}` : ''
}

/** The 3D icon a stored value names, or null (blank, a Lucide name, an emoji). */
export function chosenIcon3D(stored) {
  if (typeof stored !== 'string' || !stored.startsWith(ICON3D_PREFIX)) return null
  const name = stored.slice(ICON3D_PREFIX.length)
  return ICON3D_NAMES.includes(name) ? name : null
}

/** The icon to show: the one chosen, else one from the name, else the fallback. */
export function resolveIcon3D(stored, texts, fallback) {
  return chosenIcon3D(stored) || topicIcon(...[].concat(texts)) || fallback
}

/** The picker's groups, in order, with a plain label for each icon. */
export const ICON3D_GROUPS = [
  { label: 'Ministry', icons: [
    ['people', 'People'], ['groups', 'Groups'], ['check-ins', 'Check-ins'], ['volunteers', 'Volunteers'],
    ['giving', 'Giving'], ['baptisms', 'Baptisms'], ['salvations', 'Care and salvations'], ['campus', 'Church'],
    ['globe', 'Missions'], ['calendar', 'Events'], ['target', 'Goals'], ['organization', 'Building'],
  ] },
  { label: 'Data', icons: [
    ['dashboards', 'Dashboard'], ['metrics', 'Metrics'], ['reports', 'Report'], ['data-entry', 'Data entry'],
    ['chart-line', 'Line chart'], ['chart-area', 'Area chart'], ['chart-bar', 'Bar chart'], ['chart-pie', 'Pie chart'],
    ['chart-donut', 'Donut chart'], ['number-tile', 'Number'], ['scoreboard', 'Scoreboard'], ['stat-tiles', 'Tiles'],
    ['categories', 'Layers'], ['partitions', 'Blocks'], ['table', 'Table'], ['import', 'Import'],
  ] },
  { label: 'Workspace', icons: [
    ['favorites', 'Star'], ['assistant', 'Sparkles'], ['getting-started', 'Compass'], ['integrations', 'Connection'],
    ['settings', 'Settings'], ['location', 'Location'], ['mail', 'Mail'], ['clock', 'Schedule'], ['bell', 'Alerts'],
    ['folder', 'Folder'], ['tag', 'Tag'], ['key', 'Key'], ['lock', 'Lock'], ['billing', 'Billing'],
    ['palette', 'Palette'], ['branding', 'Brush'], ['annotations', 'Note'], ['templates', 'Templates'],
    ['check', 'Done'], ['search', 'Search'],
  ] },
]
